import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Breadcrumbs,
  Link
} from '@mui/material';
import { 
  NavigateNext as NavigateNextIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import wineRegionService, { WineRegion } from '../../../services/wineRegionService';
import { getCountryName } from '../../../utils/countryUtils';

interface FormData {
  name: string;
  localName?: string;
  parent?: string;
}

interface FormErrors {
  name?: string;
  localName?: string;
  parent?: string;
}

const RegionForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { regionId } = useParams<{ regionId?: string }>();
  const isEditMode = Boolean(regionId);
  
  // Get parent ID from query params if creating a new region
  const queryParams = new URLSearchParams(location.search);
  const parentIdFromQuery = queryParams.get('parent');
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    localName: '',
    parent: parentIdFromQuery || undefined
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [parentRegion, setParentRegion] = useState<WineRegion | null>(null);
  const [countries, setCountries] = useState<WineRegion[]>([]);
  const [loadingCountries, setLoadingCountries] = useState<boolean>(false);

  // Load region data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode && regionId) {
        setLoading(true);
        try {
          const region = await wineRegionService.getWineRegion(regionId);
          setFormData({
            name: region.name,
            localName: region.localName || '',
            parent: region.parent
          });
          
          // If region has a parent, fetch parent details
          if (region.parent) {
            const parent = await wineRegionService.getWineRegion(region.parent);
            setParentRegion(parent);
          }
        } catch (error) {
          console.error('Failed to fetch region:', error);
        } finally {
          setLoading(false);
        }
      } else if (parentIdFromQuery) {
        // If creating a new region with a parent, fetch parent details
        setLoading(true);
        try {
          const parent = await wineRegionService.getWineRegion(parentIdFromQuery);
          setParentRegion(parent);
        } catch (error) {
          console.error('Failed to fetch parent region:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [isEditMode, regionId, parentIdFromQuery]);

  // Load countries for parent selection
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await wineRegionService.getCountries(1, 100); // Get up to 100 countries
        setCountries(response.data);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };

    if (!isEditMode && !parentIdFromQuery) {
      fetchCountries();
    }
  }, [isEditMode, parentIdFromQuery]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear error when field is changed
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  
  // Handle select changes
  const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement> | any) => {
    const name = event.target.name as string;
    const value = event.target.value;
    
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Clear error when field is changed
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!isEditMode && !parentIdFromQuery && !formData.parent) {
      newErrors.parent = 'Parent region is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    try {
      if (isEditMode && regionId) {
        // Update existing region
        await wineRegionService.updateWineRegion(regionId, {
          name: formData.name,
          localName: formData.localName
        });
        navigate(`/admin/regions/${regionId}`);
      } else {
        // Create new region
        const parent = parentIdFromQuery || formData.parent;
        if (!parent) {
          throw new Error('Parent region is required');
        }
        
        // Get parent region to determine level
        const parentRegion = await wineRegionService.getWineRegion(parent);
        const newRegion = await wineRegionService.createWineRegion({
          name: formData.name,
          localName: formData.localName,
          parentId: parent
        });
        
        navigate(`/admin/regions/${newRegion._id}`);
      }
    } catch (error) {
      console.error('Failed to save region:', error);
      alert('Failed to save region. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Wine Region' : 'Create Wine Region'}
        </Typography>
        
        {/* Breadcrumbs navigation */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link 
            component={RouterLink} 
            to="/admin/regions" 
            color="inherit"
            underline="hover"
          >
            All Regions
          </Link>
          
          {parentRegion && (
            <Link
              component={RouterLink}
              to={`/admin/regions/${parentRegion._id}`}
              color="inherit"
              underline="hover"
            >
              {parentRegion.name}
            </Link>
          )}
          
          <Typography color="textPrimary">
            {isEditMode ? 'Edit Region' : 'Create Region'}
          </Typography>
        </Breadcrumbs>
        
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Region Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            margin="normal"
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
          />
          
          <TextField
            fullWidth
            label="Local Name"
            name="localName"
            value={formData.localName || ''}
            onChange={handleInputChange}
            margin="normal"
            error={Boolean(errors.localName)}
            helperText={errors.localName || 'The name in local language or display name (optional)'}
          />
          
          {!isEditMode && !parentIdFromQuery && (
            <FormControl 
              fullWidth 
              margin="normal"
              error={Boolean(errors.parent)}
            >
              <InputLabel id="parent-label">Parent Region</InputLabel>
              <Select
                labelId="parent-label"
                name="parent"
                value={formData.parent || ''}
                onChange={handleSelectChange}
                label="Parent Region"
                required
              >
                {loadingCountries ? (
                  <MenuItem disabled>Loading countries...</MenuItem>
                ) : (
                  countries.map((country) => (
                    <MenuItem key={country._id} value={country._id}>
                      {/^[A-Z]{2}$/.test(country.name) ? getCountryName(country.name) : country.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.parent && (
                <FormHelperText>{errors.parent}</FormHelperText>
              )}
            </FormControl>
          )}
          
          {parentRegion && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              This region will be created as a child of <strong>
                {/^[A-Z]{2}$/.test(parentRegion.name) ? getCountryName(parentRegion.name) : parentRegion.name}
              </strong>
            </Typography>
          )}
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Region'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate(parentRegion ? `/admin/regions/${parentRegion._id}` : '/admin/regions')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default RegionForm;
